import React, { forwardRef, useState, useEffect } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import axiosInstance from "../api/axiosInstance";
import { API_BASE_URL } from "../config/apiConfig";
import { useAuth } from "../context/AuthContext";
import "./InvoiceModal.css";

/** Normalize relative -> absolute (for API/Static file URLs) */
const toAbs = (u) => {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (API_BASE_URL || "").replace(/\/+$/, "");
  return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
};

/** Read a Blob into a data URL (PNG/JPEG supported) */
async function blobToDataUrl(blob) {
  return await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

/** Fetch image via Axios (respects auth/cookies), return data URL */
async function fetchLogoDataUrl(url) {
  if (!url) return null;
  
  try {
    //console.log("🔄 Fetching logo from:", url);
    
    const cacheBusterUrl = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
    
    const resp = await axiosInstance.get(cacheBusterUrl, {
      responseType: 'blob',
      withCredentials: true,
      timeout: 15000,
      headers: {
        'Accept': 'image/png,image/jpeg,image/*,*/*',
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!resp.data || resp.data.size === 0) {
      console.warn("❌ Empty logo blob received");
      return null;
    }
    
    //console.log("✅ Logo blob received, type:", resp.data.type, "size:", resp.data.size);
    const dataUrl = await blobToDataUrl(resp.data);
    return dataUrl;
    
  } catch (error) {
    console.warn("❌ Logo fetch failed:", error);
    return null;
  }
}

/** DEBUG function to test logo data URL */
async function testLogoDataUrl(logoDataUrl) {
  return new Promise((resolve) => {
    if (!logoDataUrl) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      //console.log("✅ Logo test passed - valid image:", img.width, "x", img.height);
      resolve(true);
    };
    img.onerror = () => {
      //console.log("❌ Logo test failed - invalid image data");
      resolve(false);
    };
    img.src = logoDataUrl;
  });
}

/** Robust getter for logo from hotelDetails prop */
function getHotelLogoUrlFromProps(hotelDetails) {
  const raw =
    hotelDetails?.logoUrl ??
    hotelDetails?.LogoUrl ??
    hotelDetails?.logoURL ??
    hotelDetails?.logo ??
    null;
  return raw ? raw : null;
}

/** Generate PDF and return as Blob for preview */
async function generatePDFBlob({ 
  logoDataUrl, 
  hotelName, 
  hotelAddress, 
  hotelPhone, 
  invoiceDate, 
  bookingNo, 
  guestName, 
  roomNumber, 
  checkInDate, 
  checkOutDate, 
  currency, 
  basePrice, 
  vatPercentage, 
  additionalLevies, 
  daysBooked, 
  totalAmount, 
  amountPaid, 
  transactionType 
}) {
  const doc = new jsPDF("p", "pt", "a4");
  const pw = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  //console.log("🎯 Starting PDF generation...");
  //console.log("📊 Logo data URL present:", !!logoDataUrl);

  // Header: logo with enhanced error handling
  let logoAdded = false;
  let logoHeight = 0;
  
  if (logoDataUrl) {
    try {
      //console.log("🖼️ Attempting to add logo to PDF...");
      
      await new Promise((resolve, reject) => {
        const testImg = new Image();
        const timeout = setTimeout(() => {
          reject(new Error("Logo loading timeout"));
        }, 5000);
        
        testImg.onload = () => {
          clearTimeout(timeout);
          //console.log("✅ Logo loaded successfully, dimensions:", testImg.width, "x", testImg.height);
          resolve();
        };
        testImg.onerror = () => {
          clearTimeout(timeout);
          console.error("❌ Logo failed to load in test");
          reject(new Error("Logo failed to load"));
        };
        testImg.src = logoDataUrl;
      });

      let imgProps;
      try {
        imgProps = doc.getImageProperties(logoDataUrl);
        //console.log("📐 Image properties:", imgProps);
      } catch (propsError) {
        console.warn("Could not get image properties:", propsError);
        imgProps = { width: 100, height: 100 };
      }
      
      const maxWidth = 80;
      const maxHeight = 80;
      const ratio = Math.min(maxWidth / imgProps.width, maxHeight / imgProps.height);
      const width = Math.max(imgProps.width * ratio, 50);
      logoHeight = Math.max(imgProps.height * ratio, 50);
      
      //console.log("📏 Logo dimensions for PDF:", width, "x", logoHeight);

      try {
        doc.addImage(logoDataUrl, "PNG", margin, y, width, logoHeight);
        logoAdded = true;
        //console.log("✅ Logo added successfully as PNG");
      } catch (pngError) {
        console.warn("PNG failed:", pngError);
        try {
          doc.addImage(logoDataUrl, margin, y, width, logoHeight);
          logoAdded = true;
          //console.log("✅ Logo added with auto-detection");
        } catch (autoError) {
          console.warn("Auto-detection failed:", autoError);
        }
      }

    } catch (imageError) {
      console.warn("❌ Logo embedding failed:", imageError);
      logoAdded = false;
      logoHeight = 0;
    }
  }

  // Hotel info - position based on whether logo was added
  const infoX = logoAdded ? margin + 100 : margin;
  const infoStartY = logoAdded ? y + (logoHeight / 2) - 10 : y;
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(hotelName, infoX, infoStartY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(hotelAddress, infoX, infoStartY + 20);
  doc.text(`Contact: ${hotelPhone}`, infoX, infoStartY + 35);

  // Calculate Y position for next section
  if (logoAdded) {
    y = Math.max(y + logoHeight, infoStartY + 45) + 25;
  } else {
    y = infoStartY + 45 + 25;
  }

  // Title + meta
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("INVOICE", margin, y);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  y += 30;

  const leftCol = margin;
  const rightCol = pw - margin - 150;
  doc.text(`Date: ${invoiceDate}`, leftCol, y);
  doc.text(`Invoice #: ${bookingNo}`, rightCol, y);
  y += 15;
  doc.text(`Guest: ${guestName}`, leftCol, y);
  doc.text(`Room: ${roomNumber}`, rightCol, y);
  y += 25;

  // Bill To + Property blocks
  const boxWidth = (pw - margin * 3) / 2;

  // Bill To
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("BILL TO", margin, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Name: ${guestName}`, margin, y + 15);
  doc.text(`Room: ${roomNumber}`, margin, y + 30);
  doc.text(`Check-In: ${checkInDate}`, margin, y + 45);
  doc.text(`Check-Out: ${checkOutDate}`, margin, y + 60);

  // Property
  const propertyX = margin + boxWidth + margin;
  doc.setFont("helvetica", "bold");
  doc.text("PROPERTY", propertyX, y);
  doc.setFont("helvetica", "normal");
  doc.text(hotelName, propertyX, y + 15);
  doc.text(hotelAddress, propertyX, y + 27);
  doc.text(`Contact: ${hotelPhone}`, propertyX, y + 39);

  y = Math.max(y + 80, y + 45) + 25;

  // Price breakdown calculations
  const vatAmount = basePrice * (vatPercentage / 100);
  const totalPerNight = basePrice + vatAmount + additionalLevies;

  // Price breakdown table
  doc.autoTable({
    startY: y,
    theme: "grid",
    styles: { 
      fontSize: 10, 
      cellPadding: 6, 
      lineColor: [0, 0, 0], 
      lineWidth: 0.1,
      font: "helvetica"
    },
    headStyles: { 
      fillColor: [245, 245, 245], 
      textColor: [0, 0, 0], 
      fontStyle: "bold",
      fontSize: 10
    },
    bodyStyles: {
      fontSize: 10
    },
    head: [["Description", "Amount"]],
    body: [
      ["Room Base Price (per night)", `${currency}${basePrice.toFixed(2)}`],
      [`VAT (${vatPercentage}%)`, `${currency}${vatAmount.toFixed(2)}`],
      ["Additional Levies (per night)", `${currency}${additionalLevies.toFixed(2)}`],
      [
        { content: "Total Per Night", styles: { fontStyle: "bold" } },
        { content: `${currency}${totalPerNight.toFixed(2)}`, styles: { fontStyle: "bold" } }
      ],
      ["Number of Nights", daysBooked.toString()],
      [
        { content: "Total Amount", styles: { fontStyle: "bold", fontSize: 11 } },
        { content: `${currency}${totalAmount.toFixed(2)}`, styles: { fontStyle: "bold", fontSize: 11 } }
      ],
      ["Transaction Type", transactionType],
    ],
    columnStyles: { 
      0: { cellWidth: "auto", fontStyle: "normal" }, 
      1: { cellWidth: 120, halign: "right", fontStyle: "normal" } 
    },
    margin: { left: margin, right: margin },
  });

  let nextY = doc.lastAutoTable.finalY + 20;

  // Payment and dates section
  doc.autoTable({
    startY: nextY,
    theme: "grid",
    styles: { 
      fontSize: 11, 
      cellPadding: 8, 
      lineColor: [0, 0, 0], 
      lineWidth: 0.1,
      font: "helvetica"
    },
    body: [
      [
        { content: "Amount Paid", styles: { fontStyle: "bold" } },
        { content: `${currency}${amountPaid.toFixed(2)}`, styles: { fontStyle: "bold", halign: "right" } }
      ],
      ["Check-In Date", checkInDate],
      ["Check-Out Date", checkOutDate],
    ],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 120, halign: "right" }
    },
    margin: { left: pw - margin - 250, right: margin },
  });

  const finalY = doc.lastAutoTable.finalY + 25;

  // Footer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.text("Thank you for staying with us!", margin, finalY);

  // Add page border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(1);
  doc.rect(margin - 10, margin - 10, pw - (margin * 2) + 20, doc.internal.pageSize.getHeight() - (margin * 2) + 20);

  //console.log("✅ PDF generation completed successfully");
  //console.log("📊 Logo was added:", logoAdded);
  
  return doc.output('blob');
}

const InvoiceModal = forwardRef(({ isOpen, onClose, guestDetails, hotelDetails }, ref) => {
  const { authData } = useAuth();
  
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // Load hotel logo and pre-process for PDF
  useEffect(() => {
    const fetchAndPrepareLogo = async () => {
      if (!isOpen) return;

      setLogoLoading(true);
      setLogoDataUrl(null);

      try {
        let finalLogoUrl = null;
        const hotelId = authData?.hotelId;

        // Strategy 1: Dedicated logo endpoint
        if (hotelId) {
          try {
            const r = await axiosInstance.get(`/Hotel/${hotelId}/logo`);
            const raw = r?.data?.logoUrl ?? r?.data?.LogoUrl;
            if (raw) {
              finalLogoUrl = raw;
              //console.log("✅ Using logo from dedicated endpoint:", finalLogoUrl);
            }
          } catch (error) {
            console.warn("Dedicated logo endpoint failed:", error);
          }
        }

        // Strategy 2: Hotel object endpoint
        if (!finalLogoUrl && hotelId) {
          try {
            const r2 = await axiosInstance.get(`/Hotel/${hotelId}`);
            const raw2 = r2?.data?.logoUrl ?? r2?.data?.LogoUrl ?? r2?.data?.logoURL;
            if (raw2) {
              finalLogoUrl = raw2;
              //console.log("✅ Using logo from hotel object:", finalLogoUrl);
            }
          } catch (error) {
            console.warn("Hotel object endpoint failed:", error);
          }
        }

        // Strategy 3: From props
        if (!finalLogoUrl) {
          const propLogo = getHotelLogoUrlFromProps(hotelDetails);
          if (propLogo) {
            finalLogoUrl = toAbs(propLogo);
            //console.log("✅ Using logo from props:", finalLogoUrl);
          }
        }

        // Set URL for display
        setLogoUrl(finalLogoUrl);

        // Pre-load for PDF
        if (finalLogoUrl) {
          //console.log("🔄 Pre-loading logo for PDF:", finalLogoUrl);
          const dataUrl = await fetchLogoDataUrl(finalLogoUrl);
          
          if (dataUrl) {
            try {
              await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = resolve;
                img.onerror = reject;
                img.src = dataUrl;
              });
              setLogoDataUrl(dataUrl);
              //console.log("✅ Logo pre-loaded for PDF");
            } catch (error) {
              console.warn("Logo validation failed:", error);
            }
          }
        }
      } catch (error) {
        console.error("Logo preparation failed:", error);
      } finally {
        setLogoLoading(false);
      }
    };

    fetchAndPrepareLogo();
  }, [authData, hotelDetails, isOpen]);

  // Clean up preview URL
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // Early return must be after all hooks
  if (!isOpen) return null;

  // Destructure fields
  const {
    guestName = "N/A",
    bookingNo = "N/A",
    roomNumber = "N/A",
    amountPaid = 0,
    totalAmount = 0,
    invoiceDate: rawInvoiceDate,
    basePrice = 0,
    vatPercentage = 0,
    additionalLevies = 0,
    daysBooked = 0,
    transactionType = "N/A",
    currency = "BWP",
    checkInDate,
    checkOutDate,
  } = guestDetails;

  // Format dates
  const invoiceDate = rawInvoiceDate
    ? new Date(rawInvoiceDate).toLocaleDateString()
    : new Date().toLocaleDateString();
  const formattedCheckInDate = checkInDate
    ? new Date(checkInDate).toLocaleDateString()
    : "N/A";
  const formattedCheckOutDate = checkOutDate
    ? new Date(checkOutDate).toLocaleDateString()
    : "N/A";

  // Price breakdown
  const vatAmount = basePrice * (vatPercentage / 100);
  const totalPerNight = basePrice + vatAmount + additionalLevies;

  const hotelName = hotelDetails?.hotelName || hotelDetails?.name || "Hotel Name";
  const hotelAddress = hotelDetails?.address || "Hotel Address";
  const hotelPhone = hotelDetails?.phoneNumber || hotelDetails?.phone || "+000 000 0000";

  const handlePreviewPDF = async () => {
    if (isGeneratingPreview) return;
    
    setIsGeneratingPreview(true);
    try {
      const pdfBlob = await generatePDFBlob({
        logoDataUrl,
        hotelName,
        hotelAddress,
        hotelPhone,
        invoiceDate,
        bookingNo,
        guestName,
        roomNumber,
        checkInDate: formattedCheckInDate,
        checkOutDate: formattedCheckOutDate,
        currency,
        basePrice,
        vatPercentage,
        additionalLevies,
        daysBooked,
        totalAmount,
        amountPaid,
        transactionType
      });

      const previewUrl = URL.createObjectURL(pdfBlob);
      
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      
      setPdfPreviewUrl(previewUrl);
      setShowPreview(true);
    } catch (error) {
      console.error("PDF preview generation failed:", error);
      alert("PDF preview generation failed: " + error.message);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const pdfBlob = await generatePDFBlob({
        logoDataUrl,
        hotelName,
        hotelAddress,
        hotelPhone,
        invoiceDate,
        bookingNo,
        guestName,
        roomNumber,
        checkInDate: formattedCheckInDate,
        checkOutDate: formattedCheckOutDate,
        currency,
        basePrice,
        vatPercentage,
        additionalLevies,
        daysBooked,
        totalAmount,
        amountPaid,
        transactionType
      });

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${bookingNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF download failed:", error);
      alert("PDF download failed: " + error.message);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  return (
    <>
      {/* Main Invoice Modal */}
      <div className="invoice-overlay">
        <div className="invoice-card" ref={ref}>
          {/* Header */}
          <div className="inv-header">
            {logoUrl && !logoLoading ? (
              <img className="inv-logo" src={logoUrl} alt="Hotel Logo" />
            ) : (
              <div className="inv-logo placeholder">
                {(hotelName && hotelName[0]) || "H"}
              </div>
            )}
            <div className="inv-hotel">
              <div className="inv-hotel-name">{hotelName}</div>
              <div className="inv-hotel-sub">{hotelAddress}</div>
              <div className="inv-hotel-sub">Contact: {hotelPhone}</div>
            </div>
          </div>

          {/* Meta */}
          <div className="inv-meta">
            <div>
              <div className="inv-title">INVOICE</div>
              <div className="inv-sub">
                Date: {invoiceDate}
              </div>
            </div>
            <div className="inv-meta-right">
              <div className="inv-sub">Invoice #: {bookingNo}</div>
              <div className="inv-sub">Room: {roomNumber}</div>
            </div>
          </div>

          {/* Two cols */}
          <div className="inv-two-col">
            <div className="inv-box">
              <div className="inv-box-title">BILL TO</div>
              <div className="inv-line">
                <b>Name:</b> {guestName}
              </div>
              <div className="inv-line">
                <b>Room:</b> {roomNumber}
              </div>
              {formattedCheckInDate && (
                <div className="inv-line">
                  <b>Check-In:</b> {formattedCheckInDate}
                </div>
              )}
              {formattedCheckOutDate && (
                <div className="inv-line">
                  <b>Check-Out:</b> {formattedCheckOutDate}
                </div>
              )}
            </div>
            <div className="inv-box">
              <div className="inv-box-title">PROPERTY</div>
              <div className="inv-line">{hotelName}</div>
              <div className="inv-line">{hotelAddress}</div>
              <div className="inv-line">Contact: {hotelPhone}</div>
            </div>
          </div>

          {/* Price Breakdown Table */}
          <table className="inv-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="amt">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Room Base Price (per night)</td>
                <td className="amt">
                  {currency}
                  {basePrice.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>VAT ({vatPercentage}%)</td>
                <td className="amt">
                  {currency}
                  {vatAmount.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>Additional Levies (per night)</td>
                <td className="amt">
                  {currency}
                  {additionalLevies.toFixed(2)}
                </td>
              </tr>
              <tr className="bold">
                <td>Total Per Night</td>
                <td className="amt">
                  {currency}
                  {totalPerNight.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>Number of Nights</td>
                <td className="amt">{daysBooked}</td>
              </tr>
              <tr className="bold big">
                <td>Total Amount</td>
                <td className="amt">
                  {currency}
                  {totalAmount.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>Transaction Type</td>
                <td className="amt">{transactionType}</td>
              </tr>
            </tbody>
          </table>

          {/* Payment Summary */}
          <div className="payment-summary">
            <div className="payment-details">
              <p><strong>Amount Paid:</strong> {currency}{amountPaid.toFixed(2)}</p>
              <p><strong>Check-In Date:</strong> {formattedCheckInDate}</p>
              <p><strong>Check-Out Date:</strong> {formattedCheckOutDate}</p>
            </div>
          </div>

          <div className="inv-footer">Thank you for staying with us!</div>

          {/* Compact Icon Buttons */}
          <div className="inv-actions-compact">
            <button 
              className="btn-icon preview" 
              onClick={handlePreviewPDF}
              disabled={isGeneratingPreview}
              title="Preview PDF"
            >
              {isGeneratingPreview ? "⏳" : "👁️"}
            </button>
            <button 
              className="btn-icon download" 
              onClick={handleDownloadPDF}
              title="Download PDF"
            >
              📥
            </button>
            <button 
              className="btn-icon close" 
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPreview && (
        <div className="pdf-preview-overlay">
          <div className="pdf-preview-modal">
            <div className="pdf-preview-header">
              <h3>PDF Preview - Invoice #{bookingNo}</h3>
              <button className="close-btn" onClick={closePreview}>×</button>
            </div>
            <div className="pdf-preview-content">
              {pdfPreviewUrl ? (
                <iframe
                  src={pdfPreviewUrl}
                  title="PDF Preview"
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                />
              ) : (
                <div className="pdf-loading">Loading PDF preview...</div>
              )}
            </div>
            <div className="pdf-preview-actions-compact">
              <button 
                className="btn-icon download" 
                onClick={handleDownloadPDF}
                title="Download PDF"
              >
                📥
              </button>
              <button 
                className="btn-icon close" 
                onClick={closePreview}
                title="Close Preview"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default InvoiceModal;